import { Journey } from '@audibene/ta.utils-opportunity'
import { AxiosError } from 'axios'
import { AppointmentType, AvailabilityMode } from '../../../../constants'
import { AvailabilitiesResponse, DayAvailability, FirstAvailability, TimeBlock } from '../../../../types'
import { HttpClientConfig } from '../../../useHttpClientConfig'
import { apiVersion, service } from '../service'
import { FilterValue, applySearchParams, urlToPathnameAndSearch } from '../utils'

interface GetAvailabilitiesRequest {
  allow_integration_errors?: boolean
  appointment_type: AppointmentType
  customer_timezone: string
  duration_in_minutes?: number
  date_end: Date
  date_start: Date
  journey: Journey
  is_verbose?: boolean
  recommendation_gid?: string
  resource_ids?: string[]
  resource_gids?: string[]
  mode?: AvailabilityMode
  use_cache?: boolean
}

const pathname = `/api/${apiVersion}/availabilities`

const makeGetAvailabilities =
  (baseUrl: string, config: HttpClientConfig) =>
  async (request: GetAvailabilitiesRequest): Promise<AvailabilitiesResponse> => {
    const url = new URL(pathname, baseUrl)

    applySearchParams(url, request as unknown as Record<string, FilterValue>)

    const pathnameAndSearch = urlToPathnameAndSearch(url)
    const payload = await service(baseUrl)
      .get<AvailabilitiesResponse>(pathnameAndSearch, config)
      .then((response): AvailabilitiesResponse => ({ ...response.data, responseStatus: response?.status }))
      .catch((error: AxiosError): AvailabilitiesResponse => {
        return {
          dayAvailability: {},
          responseStatus: error?.response?.status || 500,
          resourceHasCalendarIntegration: {},
          hasAppointmentTypeMappingError: false,
        }
      })
    return buildAvailabilityResponse(payload)
  }

const buildAvailabilityResponse = (availabilitiesResponse: AvailabilitiesResponse): AvailabilitiesResponse => {
  const { firstAvailability, dayAvailability } = availabilitiesResponse

  return {
    ...availabilitiesResponse,
    dayAvailability: buildDayAvailabilityResponse(dayAvailability),
    firstAvailability: firstAvailability ? buildFirstAvailability(firstAvailability) : undefined,
  }
}

// Convert date strings into formal date objects for TimeBlocks
const buildDayAvailabilityResponse = (dayAvailability: DayAvailability) => {
  return Object.entries(dayAvailability).reduce((acc, [key, value]) => {
    const resourceIdTimeBlocks = Object.entries(value).reduce((memo, [resourceId, blocks]) => {
      return {
        ...memo,
        [resourceId]: blocks.map(({ date_end, date_start }: TimeBlock) => ({
          date_end: new Date(date_end),
          date_start: new Date(date_start),
        })),
      }
    }, value)

    return {
      ...acc,
      [key]: resourceIdTimeBlocks,
    }
  }, dayAvailability)
}

// Convert date strings into formal date objects for FirstAvailability
const buildFirstAvailability = (firstAvailability: FirstAvailability) => {
  return firstAvailability
    ? {
        ...firstAvailability,
        start_date: new Date(firstAvailability.start_date),
      }
    : undefined
}

export { makeGetAvailabilities }
export type { GetAvailabilitiesRequest }
